import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Send, Megaphone, Trash2, Users2, Building2, X, Clock, Eye, CheckCircle2 } from 'lucide-react';
import {
  listBroadcasts, createBroadcast, deleteBroadcast, getBroadcastReads,
  listDepartments, listUsers,
} from '../../services/api';
import { toast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import MultiSelect from '../../components/MultiSelect';

const fmt = (iso) => iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—';

const BroadcastsPage = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [depts, setDepts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCompose, setShowCompose] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const canBroadcast = user?.role === 'super_admin' || user?.role === 'admin';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, d, u] = await Promise.all([
        listBroadcasts(),
        listDepartments().catch(() => ({ data: [] })),
        canBroadcast ? listUsers().catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      ]);
      setItems(b.data || []);
      setDepts(d.data || []);
      setUsers(u.data || []);
    } catch { toast({ title: 'Failed to load broadcasts', variant: 'destructive' }); }
    finally { setLoading(false); }
  }, [canBroadcast]);
  useEffect(() => { load(); }, [load]);

  const selected = items.find((i) => i.id === selectedId);

  const remove = async (b) => {
    if (!window.confirm(`Delete broadcast "${b.subject}"?`)) return;
    try { await deleteBroadcast(b.id); if (selectedId === b.id) setSelectedId(null); load(); }
    catch { toast({ title: 'Delete failed', variant: 'destructive' }); }
  };

  return (
    <div data-testid="admin-broadcasts-page">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
        <div>
          <span className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">Announcements</span>
          <h1 className="mt-2 text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>
            Broadcasts
          </h1>
          <p className="mt-2 text-sm text-[#0b1a2e]/60">
            Send an announcement to the entire company, a department, or a hand-picked list.
          </p>
        </div>
        {canBroadcast && (
          <button
            onClick={() => setShowCompose(true)}
            data-testid="new-broadcast-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#0f5c3d] text-white text-sm font-medium hover:bg-[#0b1a2e]"
          >
            <Megaphone className="w-4 h-4" /> New broadcast
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-5">
          <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
            <div className="px-4 py-3 bg-[#f7fbf8] text-[10px] uppercase tracking-widest text-[#0b1a2e]/60 font-semibold">
              {loading ? 'Loading…' : `${items.length} broadcast${items.length !== 1 ? 's' : ''}`}
            </div>
            {loading ? (
              <div className="p-8 text-center text-sm text-[#0b1a2e]/50"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…</div>
            ) : items.length === 0 ? (
              <div className="p-10 text-center text-sm text-[#0b1a2e]/50">
                <Megaphone className="w-8 h-8 mx-auto mb-3 text-[#0b1a2e]/20" />
                No broadcasts yet.
              </div>
            ) : items.map((b) => (
              <button
                key={b.id}
                onClick={() => setSelectedId(b.id)}
                data-testid="broadcast-row"
                className={`w-full text-left px-4 py-3 border-b border-black/5 hover:bg-[#f7fbf8] transition ${selectedId === b.id ? 'bg-[#f7fbf8]' : ''}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="font-medium text-sm text-[#0b1a2e] line-clamp-1">{b.subject}</div>
                  <span className="text-[10px] text-[#0b1a2e]/50 whitespace-nowrap"><Clock className="w-3 h-3 inline mr-0.5" /> {fmt(b.created_at)}</span>
                </div>
                <div className="mt-1 text-xs text-[#0b1a2e]/60 line-clamp-2">{b.body}</div>
                <div className="mt-2 flex items-center gap-2 text-[10px]">
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#e2ede6] text-[#0f5c3d] rounded font-semibold">
                    <Users2 className="w-3 h-3" /> {b.recipients_count}
                  </span>
                  {b.all_departments && <span className="px-1.5 py-0.5 bg-[#0f5c3d] text-white rounded font-semibold">ALL DEPTS</span>}
                  {b.all_assignees && <span className="px-1.5 py-0.5 bg-[#0b1a2e] text-white rounded font-semibold">ALL EMPLOYEES</span>}
                  <span className="text-[#0b1a2e]/50 ml-auto">by {b.sender_name}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7">
          {selected ? (
            <BroadcastDetail
              broadcast={selected}
              canManage={canBroadcast}
              onDelete={() => remove(selected)}
            />
          ) : (
            <div className="bg-white border border-black/5 rounded-2xl p-16 text-center text-[#0b1a2e]/50">
              <Megaphone className="w-10 h-10 mx-auto mb-3 text-[#0b1a2e]/20" />
              Pick a broadcast on the left to view details.
            </div>
          )}
        </div>
      </div>

      {showCompose && canBroadcast && (
        <ComposeBroadcast
          onClose={() => setShowCompose(false)}
          onSent={() => { setShowCompose(false); load(); }}
          depts={depts}
          users={users}
        />
      )}
    </div>
  );
};

const BroadcastDetail = ({ broadcast, canManage, onDelete }) => {
  const [reads, setReads] = useState(null);
  const [loadingReads, setLoadingReads] = useState(false);
  const [showReadPanel, setShowReadPanel] = useState(false);

  const loadReads = useCallback(async () => {
    if (!canManage) return;
    setLoadingReads(true);
    try { setReads(await getBroadcastReads(broadcast.id)); }
    catch { /* silent */ }
    finally { setLoadingReads(false); }
  }, [broadcast.id, canManage]);

  useEffect(() => { loadReads(); }, [loadReads]);

  const pct = reads && reads.total > 0 ? Math.round((reads.read_count / reads.total) * 100) : 0;

  return (
    <div className="bg-white border border-black/5 rounded-2xl p-6" data-testid="broadcast-detail">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#0f5c3d] font-semibold">Broadcast</div>
          <h2 className="mt-1 text-2xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>{broadcast.subject}</h2>
          <div className="text-xs text-[#0b1a2e]/60 mt-1">Sent {fmt(broadcast.created_at)} by {broadcast.sender_name} · reached {broadcast.recipients_count} people</div>
        </div>
        {canManage && (
          <button onClick={onDelete} className="p-1.5 rounded-lg text-red-600 hover:bg-red-50" title="Delete"><Trash2 className="w-4 h-4" /></button>
        )}
      </div>

      {canManage && reads && (
        <div className="mb-4 bg-[#f7fbf8] border border-emerald-100 rounded-xl p-4" data-testid="broadcast-read-receipts">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-[#0f5c3d] text-white flex items-center justify-center text-sm font-bold" data-testid="read-receipt-count">
                {pct}%
              </div>
              <div>
                <div className="text-sm font-semibold text-[#0b1a2e]">
                  <Eye className="w-4 h-4 inline mr-1 -mt-0.5" />
                  {reads.read_count} of {reads.total} opened
                </div>
                <div className="text-xs text-[#0b1a2e]/60">{reads.unread_count} still unread</div>
              </div>
            </div>
            <button
              onClick={() => { setShowReadPanel((s) => !s); if (!showReadPanel) loadReads(); }}
              data-testid="toggle-read-panel"
              className="text-xs font-semibold text-[#0f5c3d] hover:underline inline-flex items-center gap-1"
            >
              {showReadPanel ? 'Hide' : 'Who has read?'}
            </button>
          </div>
          {/* progress bar */}
          <div className="mt-3 h-1.5 rounded-full bg-white overflow-hidden">
            <div className="h-full bg-[#0f5c3d] transition-all" style={{ width: `${pct}%` }} />
          </div>
          {showReadPanel && (
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold mb-1">Read ({reads.read_count})</div>
                {reads.read.length === 0 ? (
                  <div className="text-[#0b1a2e]/40 text-[11px]">No one has read yet.</div>
                ) : reads.read.map((u) => (
                  <div key={u.user_id} className="flex items-center gap-1.5 py-0.5">
                    <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                    <span className="text-[#0b1a2e]">{u.name || u.email}</span>
                    {u.read_at && <span className="text-[10px] text-[#0b1a2e]/50">· {new Date(u.read_at).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })}</span>}
                  </div>
                ))}
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#0b1a2e]/50 font-semibold mb-1">Unread ({reads.unread_count})</div>
                {reads.unread.length === 0 ? (
                  <div className="text-emerald-700 text-[11px]">Everyone has read this ✨</div>
                ) : reads.unread.map((u) => (
                  <div key={u.user_id} className="flex items-center gap-1.5 py-0.5 text-[#0b1a2e]/70">
                    <span className="w-3 h-3 rounded-full border border-[#0b1a2e]/20" />
                    <span>{u.name || u.email}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {loadingReads && !reads && canManage && (
        <div className="mb-4 text-xs text-[#0b1a2e]/40"><Loader2 className="w-3 h-3 animate-spin inline mr-1" /> Loading read receipts…</div>
      )}

      <div className="text-sm text-[#0b1a2e] whitespace-pre-wrap leading-relaxed border-t border-black/5 pt-4">
        {broadcast.body}
      </div>
    </div>
  );
};

const ComposeBroadcast = ({ onClose, onSent, depts, users }) => {
  const [form, setForm] = useState({
    subject: '', body: '',
    all_departments: false, department_ids: [],
    all_assignees: false, assignee_ids: [],
    send_email: true,
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.all_departments && !form.all_assignees && form.department_ids.length === 0 && form.assignee_ids.length === 0) {
      toast({ title: 'Pick an audience', description: 'Choose at least one department, employee, or use "All".', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const r = await createBroadcast(form);
      toast({ title: 'Broadcast sent', description: `Reached ${r.recipients_count} recipient${r.recipients_count !== 1 ? 's' : ''}` });
      onSent();
    } catch (err) {
      toast({ title: 'Send failed', variant: 'destructive', description: err?.response?.data?.detail || '' });
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <form onSubmit={submit} className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-black/5 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#0f5c3d] font-semibold">Compose</div>
            <div className="text-lg font-semibold text-[#0b1a2e]">New broadcast</div>
          </div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-black/5 rounded-lg"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <label className="block">
            <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Subject</span>
            <input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="broadcast-subject" className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2.5 text-sm" placeholder="e.g. Office closed 15th Feb" />
          </label>
          <label className="block">
            <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Message</span>
            <textarea required rows={6} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="broadcast-body" className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2.5 text-sm resize-y" placeholder="Write the announcement…" />
          </label>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#0b1a2e]/60 font-semibold mb-2">
              <Building2 className="w-3 h-3 inline mr-1 -mt-0.5" /> Departments
            </div>
            <MultiSelect
              testId="broadcast-departments"
              placeholder="Pick departments…"
              options={depts.map((d) => ({ value: d.id, label: d.name }))}
              values={form.department_ids}
              onChange={(v) => setForm({ ...form, department_ids: v })}
              allOption={{
                label: 'All departments',
                checked: form.all_departments,
                onToggle: (v) => setForm({ ...form, all_departments: v, department_ids: v ? [] : form.department_ids }),
              }}
              emptyLabel="No department selected"
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#0b1a2e]/60 font-semibold mb-2">
              <Users2 className="w-3 h-3 inline mr-1 -mt-0.5" /> Individual employees (optional — combined with dept picks)
            </div>
            <MultiSelect
              testId="broadcast-assignees"
              placeholder="Pick individuals…"
              options={users.map((u) => ({ value: u.user_id, label: u.name, hint: u.role }))}
              values={form.assignee_ids}
              onChange={(v) => setForm({ ...form, assignee_ids: v })}
              allOption={{
                label: 'All employees',
                checked: form.all_assignees,
                onToggle: (v) => setForm({ ...form, all_assignees: v, assignee_ids: v ? [] : form.assignee_ids }),
              }}
              emptyLabel="No individual selected"
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.send_email} onChange={(e) => setForm({ ...form, send_email: e.target.checked })} className="accent-[#0f5c3d]" />
            Also send email to recipients (unticked = in-app inbox only)
          </label>
        </div>
        <div className="px-6 py-4 border-t border-black/5 bg-[#f7fbf8] flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm hover:bg-black/5">Cancel</button>
          <button type="submit" disabled={saving} data-testid="submit-broadcast" className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-[#0f5c3d] text-white text-sm font-medium disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send broadcast
          </button>
        </div>
      </form>
    </div>
  );
};

export default BroadcastsPage;
